import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Calendar as CalendarIcon, MapPin, Share2, Navigation, Clock } from 'lucide-react';
import { format, setHours, setMinutes } from 'date-fns';
import { useGeolocation } from '@/hooks/use-geolocation';

const MeetingTypes = [
  { value: 'First', label: 'First Meeting' },
  { value: 'Second', label: 'Second Meeting' },
  { value: 'Final', label: 'Final Meeting' },
];

const TIME_SLOTS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return [
    { value: `${hour}:00`, label: `${hour}:00` },
    { value: `${hour}:30`, label: `${hour}:30` },
  ];
}).flat();

const NewMeeting = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: new Date().toISOString(),
    time: '09:00',
    meetingType: 'First',
    location: '',
    location_lat: 0,
    location_lng: 0,
    radius: 200,
  });

  const { 
    position,
    error: locationError,
    loading: locationLoading,
    permissionStatus,
    requestGeolocation
  } = useGeolocation();

  const handleUseCurrentLocation = useCallback(async () => {
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
          enableHighAccuracy: true,
          maximumAge: 0
          }
        );
      });

      setFormData(prev => ({
        ...prev,
        location_lat: position.coords.latitude,
        location_lng: position.coords.longitude,
        location: 'Current Location'
      }));
      toast.success('Location set successfully!', {
        duration: 3000, // Show for 3 seconds
      });
    } catch (error) {
      console.error('Error getting location:', error);
      if (error instanceof GeolocationPositionError) {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location permission denied. Please enable location access in your browser settings and try again.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information is unavailable. Please check your device\'s location services and try again.');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out. Please try again.');
            break;
          default:
            toast.error('Unable to get your location. Please try again.');
        }
      } else {
        toast.error('Error getting location. Please try again.');
      }
    }
  }, []);

  // Add a function to handle location permission request
  const requestLocationPermission = useCallback(async () => {
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      
      if (permission.state === 'denied') {
        toast.error('Location permission denied. Please enable location access in your browser settings.');
        return false;
      }
      
      if (permission.state === 'prompt') {
        toast.info('Please allow location access when prompted.');
      }
      
      return true;
    } catch (error) {
      console.error('Error checking location permission:', error);
      toast.error('Error checking location permission. Please try again.');
      return false;
    }
  }, []);

  // Update the location button click handler
  const handleLocationButtonClick = async () => {
    const hasPermission = await requestLocationPermission();
    if (hasPermission) {
      handleUseCurrentLocation();
    }
  };

  // Update location when position changes
  useEffect(() => {
    if (position?.latitude && position?.longitude && formData.location === 'Current Location') {
      setFormData(prev => ({
        ...prev,
        location_lat: position.latitude,
        location_lng: position.longitude
      }));
    }
  }, [position]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !user?.group_id) {
      toast.error('User information not found');
      return;
    }

    // Validate all required fields
    if (!formData.title.trim()) {
      toast.error('Please enter a meeting title');
      return;
    }

    if (!formData.date) {
      toast.error('Please select a meeting date');
      return;
    }

    if (!formData.time) {
      toast.error('Please select a meeting time');
      return;
    }

    if (!formData.meetingType) {
      toast.error('Please select a meeting type');
      return;
    }

    if (!formData.location_lat || !formData.location_lng) {
      toast.error('Please set the meeting location');
      return;
    }

    if (!formData.radius) {
      toast.error('Please set the attendance radius');
      return;
    }

    setLoading(true);
    try {
      // Parse the date and time
      const meetingDate = new Date(formData.date);
      const [hours, minutes] = formData.time.split(':').map(Number);
      
      // Set the time on the meeting date
      meetingDate.setHours(hours, minutes, 0, 0);

      // Format the datetime in ISO format
      const formattedDateTime = meetingDate.toISOString();

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/meetings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title.trim(),
          date: formattedDateTime,
          time: formData.time,
          meetingType: formData.meetingType,
          location: formData.location || 'Current Location',
          location_lat: formData.location_lat,
          location_lng: formData.location_lng,
          radius: formData.radius,
          group_id: user.group_id,
          admin_id: user.id
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create meeting');
      }

      toast.success('Meeting created successfully!');
      
      // Generate member attendance URL
      const memberAttendanceUrl = `${window.location.origin}/members/${user.id}`;
      
      // Show member attendance URL in a toast
      toast.info(
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="font-medium">Member Attendance URL:</p>
            <code className="block p-2 bg-[#F5E6C3] text-[#D4AF37] rounded text-sm break-all">
              {memberAttendanceUrl}
            </code>
            <Button
              variant="outline"
              size="sm"
              className="w-full border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white"
              onClick={() => {
                navigator.clipboard.writeText(memberAttendanceUrl);
                toast.success('Member attendance URL copied!');
              }}
            >
              Copy Member URL
            </Button>
          </div>
        </div>,
        {
          duration: 10000, // Show for 10 seconds
        }
      );
      
      // Automatically copy the member attendance URL to clipboard
      try {
        await navigator.clipboard.writeText(memberAttendanceUrl);
        toast.success('Member attendance URL copied to clipboard!');
      } catch (error) {
        console.error('Failed to copy member attendance URL:', error);
      }
      
      // Open member attendance page in new tab after a short delay
      setTimeout(() => {
        window.open(memberAttendanceUrl, '_blank');
      }, 1000);
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create meeting');
    } finally {
      setLoading(false);
    }
  };

  // Update the date selection handler
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      // Ensure the date is set to midnight UTC
      const utcDate = new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0, 0, 0, 0
      ));
      setFormData(prev => ({ ...prev, date: utcDate.toISOString() }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="container max-w-3xl py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl">Create New Meeting</CardTitle>
            <CardDescription>Create a new CDS meeting and generate an attendance link</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Meeting Title</Label>
                <Input
                  id="title"
                  name="title"
                  placeholder="Monthly CDS General Meeting"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Meeting Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.date ? format(new Date(formData.date), 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.date ? new Date(formData.date) : undefined}
                        onSelect={handleDateSelect}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="time">Meeting Time</Label>
                  <Select
                    value={formData.time}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, time: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((slot) => (
                        <SelectItem key={slot.value} value={slot.value}>
                          {slot.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-3">
                <Label>Meeting Type</Label>
                <RadioGroup value={formData.meetingType} onValueChange={(value) => setFormData(prev => ({ ...prev, meetingType: value }))} className="flex flex-col space-y-1">
                  {MeetingTypes.map((type) => (
                    <div key={type.value} className="flex items-center space-x-2">
                      <RadioGroupItem value={type.value} id={`type-${type.value}`} />
                      <Label htmlFor={`type-${type.value}`}>{type.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-4">
                <Label>Location Settings</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleLocationButtonClick}
                    disabled={locationLoading}
                    className="flex items-center gap-2"
                  >
                    {locationLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Navigation className="h-4 w-4" />
                    )}
                    {locationLoading ? 'Getting Location...' : 'Use Current Location'}
                  </Button>
                  {locationError && (
                    <span className="text-sm text-red-500">{locationError}</span>
                  )}
                </div>

                {formData.location && (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span>Current location: {formData.location_lat.toFixed(6)}, {formData.location_lng.toFixed(6)}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Attendance Radius (meters)</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[formData.radius]}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, radius: value[0] }))}
                      min={50}
                      max={1000}
                      step={50}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium w-16 text-right">
                      {formData.radius}m
                    </span>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !formData.location_lat || !formData.location_lng}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Meeting...
                  </>
                ) : (
                  'Create Meeting'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default NewMeeting;